import { useFormikContext } from 'formik'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@ui/components/Button'
import { ContentBlock } from '@ui/components/ContentBlock/ContentBlock'
import { FeatureIcon } from '@ui/components/FeatureIcon/FeatureIcon'
import { CircleCheckIcon } from '@ui/icons/CircleCheck.icon'

import { useWindowResize } from '@utils/domUtils'

import {
  isPaymentProviderPSPType,
  isPaymentProviderPaymentAgentType,
} from '@model/PaymentProviderType'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box'
import { FullScreenFooter } from '@shared/templates/FullScreenLayout'
import { FundingTemplate } from '@shared/templates/FundingTemplate'

import type { DepositFormValues } from '../DepositFlowLayout/types'

export const DepositSuccessContent: React.FC<{
  providerCategoryId?: number
  onViewHistory: () => void
  onNewDeposit: () => void
}> = ({ providerCategoryId, onViewHistory, onNewDeposit }) => {
  const { t } = useTranslation()
  const isMobile = useWindowResize()
  const { values } = useFormikContext<DepositFormValues>()
  const { formatMoney } = useFormatNumber()
  const contentRef = useRef<HTMLDivElement>(null)

  const isPA = isPaymentProviderPaymentAgentType(providerCategoryId)
  const isPSP = isPaymentProviderPSPType(providerCategoryId)

  const provider = values?.paymentProvider
  const amountValue = Number(values?.amount || 0)
  const summaryCurrency =
    provider?.currency?.id ||
    values?.wallet?.currency?.id ||
    values?.tradingAccount?.currency?.id ||
    'USD'

  const destinationLabel = values?.wallet
    ? t('Wallet.Wallet Currency Name', {
        walletCurrencyId: values.wallet.currency.id,
        walletName: values.wallet.name,
      })
    : `${values?.tradingAccount?.currency?.id}-${values?.tradingAccount?.name ?? ''}`

  return (
    <Box id='tr-wallet-deposit-succeed' fullHeight>
      <FundingTemplate contentRef={contentRef}>
        <Box flex direction='column' gap='x32' align='center' style={{ minHeight: '100%' }}>
          <ContentBlock
            variant='voucher'
            icon={
              <FeatureIcon
                size='lg'
                icon={CircleCheckIcon}
                bg='surface-success-high-em'
                color='text-white'
              />
            }
            title={t('Deposit Successful')}
            description={t(
              'Processing in progress Your balance will be updated once confirmed by the payment provider'
            )}
          />

          {(isPA || isPSP) && (
            <ContentBlock
              variant='list'
              rows={[
                {
                  label: values?.wallet ? t('Wallet.To') : t('Trading Account.To'),
                  value: destinationLabel,
                },
                {
                  label: t('Amount'),
                  value: formatMoney(amountValue, summaryCurrency),
                },
                {
                  label: t('Wallet.Payment Method'),
                  value: provider?.description || provider?.name || '',
                },
              ]}
            />
          )}

          <FullScreenFooter contentRef={contentRef}>
            <Box
              flex
              direction={isMobile ? 'column' : 'row'}
              align='center'
              gap='x16'
              justify='center'
              maxWidth={480}
              fullWidth
            >
              <Button.Primary size='lg' fullWidth onClick={onViewHistory}>
                {t('View History')}
              </Button.Primary>
              <Button.Secondary size='lg' fullWidth onClick={onNewDeposit}>
                {t('New Deposit')}
              </Button.Secondary>
            </Box>
          </FullScreenFooter>
        </Box>
      </FundingTemplate>
    </Box>
  )
}
